import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Mail, AlertTriangle, Check, Clock, X, RefreshCw, Eye, RotateCw, Filter, MessageCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationOutbox() {
  const [searchParams] = useSearchParams();
  const condominiumId = searchParams.get('condominium');
  
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const { token } = useAuth();

  const pageSize = 20;

  useEffect(() => {
    fetchData();
  }, [token, condominiumId, page, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let query = `?limit=${pageSize}&offset=${page * pageSize}`;
      if (condominiumId) query += `&condominium_id=${condominiumId}`;
      if (statusFilter) query += `&status=${statusFilter}`;

      const [entriesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/notifications/outbox${query}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/notifications/outbox/stats${condominiumId ? `?condominium_id=${condominiumId}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (entriesRes.ok) {
        const data = await entriesRes.json();
        setEntries(data.entries || []);
        setTotal(data.total || 0);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const retryNotification = async (id) => {
    setRetrying(id);
    try {
      const res = await fetch(`${API_URL}/api/notifications/outbox/${id}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Notifica reinviata');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Errore nel reinvio');
      }
    } catch (error) {
      toast.error('Errore nel reinvio');
    } finally {
      setRetrying(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      SENT: { color: 'bg-green-100 text-green-800', icon: <Check className="h-3 w-3" />, label: 'Inviato' },
      QUEUED: { color: 'bg-blue-100 text-blue-800', icon: <Clock className="h-3 w-3" />, label: 'In coda' },
      FAILED: { color: 'bg-red-100 text-red-800', icon: <X className="h-3 w-3" />, label: 'Fallito' },
      SKIPPED: { color: 'bg-gray-100 text-gray-800', icon: <AlertTriangle className="h-3 w-3" />, label: 'Saltato' }
    };
    const c = config[status] || config.QUEUED;
    return (
      <Badge className={`${c.color} flex items-center gap-1`}>
        {c.icon}
        {c.label}
      </Badge>
    );
  };

  const getChannelIcon = (channel) => {
    if (channel === 'WHATSAPP_LINK') return <MessageCircle className="h-4 w-4 text-green-600" />;
    return <Mail className="h-4 w-4 text-blue-600" />;
  };

  const getTemplateLabel = (key) => {
    const labels = {
      PAYMENT_REMINDER: 'Promemoria Pagamento',
      OVERDUE_NOTICE: 'Sollecito',
      PAYMENT_RECEIVED: 'Conferma Pagamento',
      DOC_PUBLISHED: 'Documento',
      MEETING_NOTICE: 'Convocazione',
      MEETING_REMINDER: 'Promemoria Assemblea',
      TRIAL_EXPIRING: 'Trial in Scadenza',
      PLAN_EXPIRED: 'Piano Scaduto'
    };
    return labels[key] || key;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifiche Inviate"
        subtitle="Outbox delle notifiche"
        actions={
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-gray-500">Totale</p>
              </div>
              <Mail className="h-8 w-8 text-gray-300" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.SENT}</p>
                <p className="text-sm text-gray-500">Inviate</p>
              </div>
              <Check className="h-8 w-8 text-green-300" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.QUEUED}</p>
                <p className="text-sm text-gray-500">In coda</p>
              </div>
              <Clock className="h-8 w-8 text-blue-300" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.FAILED}</p>
                <p className="text-sm text-gray-500">Fallite</p>
              </div>
              <X className="h-8 w-8 text-red-300" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-gray-600">{stats.SKIPPED}</p>
                <p className="text-sm text-gray-500">Saltate</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-gray-300" />
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-500">Filtra per stato:</span>
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(0); }}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="SENT">Inviate</SelectItem>
                <SelectItem value="QUEUED">In coda</SelectItem>
                <SelectItem value="FAILED">Fallite</SelectItem>
                <SelectItem value="SKIPPED">Saltate</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna notifica trovata</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Destinatario</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Oggetto</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="w-24">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{getChannelIcon(entry.channel)}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{entry.recipient_name || '—'}</p>
                        <p className="text-xs text-gray-500">{entry.recipient_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{getTemplateLabel(entry.template_key)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm truncate max-w-[200px] block">{entry.subject}</span>
                    </TableCell>
                    <TableCell>{getStatusBadge(entry.status)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-500">
                        {formatDate(entry.sent_at || entry.created_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => setSelectedEntry(entry)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {entry.status === 'FAILED' && entry.retries < entry.max_retries && (
                          <Button 
                            size="icon" 
                            variant="ghost"
                            onClick={() => retryNotification(entry.id)}
                            disabled={retrying === entry.id}
                          >
                            <RotateCw className={`h-4 w-4 ${retrying === entry.id ? 'animate-spin' : ''}`} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-gray-500">
              Mostrando {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} di {total}
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                Precedente
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                Successiva
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedEntry} onOpenChange={() => setSelectedEntry(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEntry && getChannelIcon(selectedEntry.channel)}
              Dettaglio Notifica
            </DialogTitle>
            <DialogDescription>
              {selectedEntry && getTemplateLabel(selectedEntry.template_key)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedEntry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Destinatario</p>
                  <p>{selectedEntry.recipient_name}</p>
                  <p className="text-sm text-gray-500">{selectedEntry.recipient_email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Stato</p>
                  <div className="mt-1">{getStatusBadge(selectedEntry.status)}</div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Creata</p>
                  <p className="text-sm">{formatDate(selectedEntry.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Inviata</p>
                  <p className="text-sm">{formatDate(selectedEntry.sent_at)}</p>
                </div>
              </div>

              {selectedEntry.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-800">Errore</p>
                  <p className="text-sm text-red-600">{selectedEntry.error}</p>
                </div>
              )}

              {selectedEntry.skip_reason && (
                <div className="bg-gray-50 border rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-800">Motivo skip</p>
                  <p className="text-sm text-gray-600">{selectedEntry.skip_reason}</p>
                </div>
              )}

              {selectedEntry.whatsapp_link && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-green-800 mb-2">Link WhatsApp</p>
                  <a 
                    href={selectedEntry.whatsapp_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-green-600 hover:underline break-all"
                  >
                    {selectedEntry.whatsapp_link.substring(0, 80)}...
                  </a>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">Oggetto</p>
                <p className="font-medium">{selectedEntry.subject}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">Contenuto</p>
                <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-sm max-h-64 overflow-y-auto">
                  {selectedEntry.body}
                </div>
              </div>

              {selectedEntry.status === 'FAILED' && selectedEntry.retries < selectedEntry.max_retries && (
                <div className="flex justify-end">
                  <Button onClick={() => { retryNotification(selectedEntry.id); setSelectedEntry(null); }}>
                    <RotateCw className="h-4 w-4 mr-2" />
                    Riprova invio
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
